import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Summary {
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  pendingTasks: number;
  completionPercent: number;
}

export default function CompliancePanel({
  branchId,
  staffId,
}: {
  branchId: string;
  staffId?: string;
}) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [certs, setCerts] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', priority: 'medium', dueDate: new Date().toISOString().split('T')[0] });

  const load = async () => {
    if (!branchId) return;
    try {
      const [dash, taskList] = await Promise.all([
        apiClient.getComplianceDashboard(branchId),
        apiClient.getComplianceTasks(branchId),
      ]);
      setSummary(dash.data.summary || null);
      setCerts(dash.data.expiringCertificates || []);
      setTasks(Array.isArray(taskList.data) ? taskList.data : []);
      setErr('');
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not load compliance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const addTask = async () => {
    if (!newTask.title.trim()) {
      setErr('Give the check a name.');
      return;
    }
    try {
      await apiClient.createComplianceTask(branchId, {
        title: newTask.title.trim(),
        dueDate: newTask.dueDate,
        priority: newTask.priority,
        category: 'health_safety',
        frequency: 'daily',
      });
      setNewTask({ ...newTask, title: '' });
      setShowAdd(false);
      await load();
    } catch (e: any) {
      const d = e?.response?.data?.message;
      setErr(Array.isArray(d) ? d.join(', ') : d || 'Could not add the check');
    }
  };

  const complete = async (taskId: string) => {
    if (!staffId) return;
    setBusyId(taskId);
    try {
      await apiClient.completeComplianceTask(taskId, staffId);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not complete task');
    } finally {
      setBusyId('');
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading compliance…</div>;

  const s = summary || { totalTasks: 0, completedTasks: 0, overdueTasks: 0, pendingTasks: 0, completionPercent: 0 };
  // Backend stores status lowercase ('scheduled' | 'completed')
  const isDone = (t: any) => String(t.status).toLowerCase() === 'completed';
  const openTasks = tasks.filter((t) => !isDone(t));
  const doneTasks = tasks.filter(isDone);

  return (
    <div className="space-y-6">
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Completion</h3>
          <p className="mt-1 text-3xl font-bold text-green-700 tabular-nums">{s.completionPercent}%</p>
          <p className="text-xs text-gray-500 mt-1 tabular-nums">
            {s.completedTasks} of {s.totalTasks} tasks
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Overdue</h3>
          <p className={`mt-1 text-3xl font-bold tabular-nums ${s.overdueTasks > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {s.overdueTasks}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Pending</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{s.pendingTasks}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Certs expiring</h3>
          <p className={`mt-1 text-3xl font-bold tabular-nums ${certs.length > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
            {certs.length}
          </p>
          <p className="text-xs text-gray-500 mt-1">within 30 days</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Open tasks</h3>
            <div className="flex gap-3 items-center">
              <button
                onClick={() => setShowAdd(v => !v)}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-3 py-1.5 rounded-lg text-sm"
              >
                {showAdd ? 'Cancel' : '+ Add check'}
              </button>
              <button onClick={load} className="text-sm font-medium text-amber-700 hover:text-amber-900">
                Refresh
              </button>
            </div>
          </div>
          {showAdd && (
            <div className="px-6 py-4 border-b bg-amber-50/40 grid grid-cols-1 sm:grid-cols-[1fr_130px_130px_auto] gap-3 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">What needs checking</label>
                <input
                  value={newTask.title}
                  onChange={e => setNewTask(t => ({ ...t, title: e.target.value }))}
                  placeholder="e.g. Fridge temperature log"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due</label>
                <input
                  type="date"
                  value={newTask.dueDate}
                  onChange={e => setNewTask(t => ({ ...t, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={newTask.priority}
                  onChange={e => setNewTask(t => ({ ...t, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <button onClick={addTask} className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-4 py-2 rounded-lg text-sm">
                Add
              </button>
            </div>
          )}

          <div className="px-6 py-2">
            {openTasks.length === 0 && doneTasks.length === 0 && (
              <div className="py-10 text-center">
                <p className="text-gray-500">No compliance tasks have been set up yet.</p>
                <p className="text-sm text-gray-400 mt-2">
                  Daily cleaning checks, temperature logs and inspections will appear here once created.
                </p>
              </div>
            )}
            {openTasks.map((t) => {
              // A check due today is not overdue until the day is out —
              // must match the backend's rule or the chips contradict the summary
              const endOfDue = t.dueDate ? new Date(t.dueDate) : null;
              if (endOfDue) endOfDue.setHours(23, 59, 59, 999);
              const overdue = !!endOfDue && endOfDue < new Date();
              return (
                <div key={t.id} className="flex items-center gap-3 py-3 border-b last:border-0">
                  <button
                    onClick={() => complete(t.id)}
                    disabled={busyId === t.id || !staffId}
                    className="w-5 h-5 rounded border-2 border-gray-300 hover:border-amber-500 disabled:opacity-40 shrink-0"
                    aria-label={`Mark ${t.title || 'task'} complete`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{t.template?.name || t.title || 'Task'}</p>
                    <p className="text-xs text-gray-500">
                      {t.dueDate ? `Due ${new Date(t.dueDate).toLocaleDateString('en-ZA', { dateStyle: 'medium' })}` : 'No due date'}
                      {t.assignedToName ? ` · ${t.assignedToName}` : ''}
                    </p>
                  </div>
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${
                      overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {overdue ? 'overdue' : 'due'}
                  </span>
                </div>
              );
            })}
            {doneTasks.slice(0, 5).map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-3 border-b last:border-0 opacity-60">
                <span className="w-5 h-5 rounded bg-green-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-500 text-sm line-through truncate">
                    {t.template?.name || t.title || 'Task'}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t.completedByStaff?.name ? `${t.completedByStaff.name} · ` : ''}
                    {t.completedDate ? new Date(t.completedDate).toLocaleString('en-ZA', { timeStyle: 'short', dateStyle: 'short' }) : ''}
                  </p>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-800 shrink-0">done</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 px-6 py-4 border-b">Certificates expiring</h3>
          <div className="px-6 py-2">
            {certs.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-gray-500">No certificates expiring in the next 30 days.</p>
                <p className="text-sm text-gray-400 mt-2">Food handling and first aid certificates are tracked here.</p>
              </div>
            ) : (
              certs.map((c: any, i: number) => {
                const days = c.expiryDate
                  ? Math.ceil((new Date(c.expiryDate).getTime() - Date.now()) / 86400000)
                  : null;
                return (
                  <div key={c.id || i} className="flex items-center justify-between py-3 border-b last:border-0">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{c.staffName || c.staff?.name || 'Staff'}</p>
                      <p className="text-xs text-gray-500">{c.certificateName || c.name || 'Certificate'}</p>
                    </div>
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        days !== null && days <= 14 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {days !== null ? `${days} days` : 'expiring'}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
